import { format, parseISO } from "date-fns";
import { Paperclip } from "lucide-react";
import { describeEntry, ledgerBrokers } from "@vaulzo/core/domain/stocks";
import { BADGE_CLASSES, Chip } from "./ui";

const TYPE_FILTERS = [
  { value: "all", label: "All" },
  { value: "convert", label: "Convert" },
  { value: "buy", label: "Buy" },
  { value: "sell", label: "Sell" },
  { value: "transfer", label: "Transfer" },
];

const sameBroker = (a, b) => (a || "").toLowerCase() === (b || "").toLowerCase();

/** Activity view: the portfolio's entries, newest first, filterable by
 *  broker and type (both apply). Clicking one opens it for editing. */
export default function ActivityTab({ transactions, loading, filters, setFilters, onSelect }) {
  const brokers = ledgerBrokers(null, transactions);
  const rows = transactions.filter(
    (t) =>
      (filters.type === "all" || t.type === filters.type) &&
      (filters.broker === "all" || sameBroker(t.broker, filters.broker) || sameBroker(t.to_broker, filters.broker))
  );

  return (
    <div className="flex flex-col gap-4 px-7 pb-8 pt-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-[6px]">
          <Chip active={filters.broker === "all"} onClick={() => setFilters({ ...filters, broker: "all" })}>
            All brokers
          </Chip>
          {brokers.map((b) => (
            <Chip key={b} active={sameBroker(filters.broker, b)} onClick={() => setFilters({ ...filters, broker: b })}>
              {b}
            </Chip>
          ))}
        </div>
        <div className="flex flex-wrap gap-[6px]">
          {TYPE_FILTERS.map((t) => (
            <Chip key={t.value} active={filters.type === t.value} onClick={() => setFilters({ ...filters, type: t.value })}>
              {t.label}
            </Chip>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-pf-line bg-white">
        {loading ? (
          <Empty>Loading entries…</Empty>
        ) : rows.length === 0 ? (
          <Empty>{transactions.length ? "No entries match these filters." : "No entries yet."}</Empty>
        ) : (
          rows.map((t) => {
            const { label, title, sub, amount } = describeEntry(t);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => onSelect(t)}
                className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-[14px] gap-y-1 border-b border-pf-divider px-[18px] py-[13px] text-left transition-colors last:border-b-0 hover:bg-pf-hover"
              >
                <span
                  className={`min-w-[70px] whitespace-nowrap rounded-full px-[9px] py-[3px] text-center text-[11px] font-semibold ${BADGE_CLASSES[t.type] || BADGE_CLASSES.transfer}`}
                >
                  {label}
                </span>
                <span className="flex min-w-0 flex-col gap-[2px]">
                  <span className="text-[14px] font-medium">{title}</span>
                  <span className="truncate text-[12px] text-pf-faint">{sub}</span>
                </span>
                <span className="flex flex-col items-end gap-[2px]">
                  <span className="whitespace-nowrap text-[14px] font-semibold">{amount}</span>
                  <span className="flex items-center gap-[6px] whitespace-nowrap text-[12px] text-pf-faint">
                    {t.attachment_count > 0 && (
                      <span className="flex items-center gap-[2px]" title={`${t.attachment_count} proof file(s)`}>
                        <Paperclip className="h-3 w-3" strokeWidth={2} />
                        {t.attachment_count}
                      </span>
                    )}
                    {format(parseISO(t.date), "d MMM yyyy")}
                  </span>
                </span>
              </button>
            );
          })
        )}
      </div>
      <div className="text-center text-[12px] text-pf-faint">
        Click an entry to edit it. Portfolio updates from these entries.
      </div>
    </div>
  );
}

function Empty({ children }) {
  return <div className="p-8 text-center text-[13px] text-pf-faint">{children}</div>;
}
